import ReactPlayer from 'react-player';
import { FaPlay } from 'react-icons/fa';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { LectureVideo, VideoQuality } from '../../lib/types';
import {
    clearPreferredVideoQualityHeight,
    getInitialVideoQuality,
    getVideoQualities,
    hasMultipleVideoQualities,
    setPreferredVideoQualityHeight,
} from '../../lib/video';
import { VideoQualityMenu } from './VideoQualityMenu';

interface LectureVideoPlayerProps {
    isActive: boolean | undefined;
    video: LectureVideo;
    onEnded?: () => void;
    onFirstPlay?: () => void;
    autoPlay?: boolean;
    posterClassName?: string;
}

export function LectureVideoPlayer({
    isActive,
    video,
    onEnded,
    onFirstPlay,
    autoPlay,
    posterClassName,
}: LectureVideoPlayerProps) {
    const playerRef = useRef<HTMLVideoElement>(null);
    const pendingSeekRef = useRef<{ time: number; play: boolean } | null>(null);
    const hasStartedRef = useRef(false);

    const [playing, setPlaying] = useState(false);
    const [showPoster, setShowPoster] = useState(true);
    const [wasPlayed, setWasPlayed] = useState(false);
    const [isQualityMenuOpen, setIsQualityMenuOpen] = useState(false);
    const [selectedQuality, setSelectedQuality] = useState<VideoQuality>(() =>
        getInitialVideoQuality(video)
    );

    const qualities = getVideoQualities(video);
    const showQualityMenu = hasMultipleVideoQualities(video);

    const onEndedExtended = () => {
        setPlaying(false);
        if (onEnded) onEnded();
    };

    const onPlayExtended = () => {
        setWasPlayed(true);
        setPlaying(true);
    };

    const handlePosterPlay = () => {
        setShowPoster(false);
        setWasPlayed(true);

        if (!hasStartedRef.current) {
            hasStartedRef.current = true;
            onFirstPlay?.();
        }

        setPlaying(true);
    };

    useEffect(() => {
        const shouldPlay = autoPlay ?? false;
        setPlaying(shouldPlay);
        setShowPoster(!shouldPlay);
    }, [isActive, autoPlay]);

    useEffect(() => {
        setSelectedQuality(getInitialVideoQuality(video));
        setIsQualityMenuOpen(false);
        setWasPlayed(false);
        setShowPoster(true);
        hasStartedRef.current = false;
    }, [video.url, video.qualities]);

    const applyVideoRestrictions = () => {
        const videoElement = playerRef.current;
        if (!videoElement) return;

        videoElement.setAttribute(
            'controlsList',
            'nodownload noplaybackrate'
        );
        videoElement.setAttribute('disablePictureInPicture', 'true');
        videoElement.setAttribute('onContextMenu', 'return false;');
    };

    const handleReady = () => {
        const videoElement = playerRef.current;
        if (!videoElement) return;

        applyVideoRestrictions();

        if (pendingSeekRef.current) {
            const { time, play } = pendingSeekRef.current;
            videoElement.currentTime = time;

            if (play) {
                videoElement.play().catch(() => undefined);
            }

            pendingSeekRef.current = null;
        }
    };

    const handleQualityChange = (quality: VideoQuality) => {
        if (quality.height === selectedQuality.height) return;

        const videoElement = playerRef.current;
        pendingSeekRef.current = {
            time: videoElement?.currentTime ?? 0,
            play: playing,
        };

        setSelectedQuality(quality);
        setPreferredVideoQualityHeight(quality.height);
    };

    const handleError = () => {
        const fallback = getVideoQualities(video)[0];

        if (!fallback?.url || fallback.url === selectedQuality.url) return;

        if (selectedQuality.height !== 0) {
            clearPreferredVideoQualityHeight();
        }

        pendingSeekRef.current = {
            time: playerRef.current?.currentTime ?? 0,
            play: playing,
        };
        setSelectedQuality(fallback);
    };

    return (
        <div className="flex-1 overflow-hidden">
            {isActive ? (
                <div className="group relative mt-4 aspect-video w-full overflow-hidden rounded-xl bg-cloud-100">
                    <ReactPlayer
                        ref={playerRef}
                        src={showPoster ? undefined : selectedQuality.url}
                        playing={playing}
                        preload={showPoster ? 'none' : 'metadata'}
                        controls={true}
                        width="100%"
                        height="100%"
                        onEnded={onEndedExtended}
                        onPlay={onPlayExtended}
                        onPause={() => setPlaying(false)}
                        onReady={handleReady}
                        onError={handleError}
                    />

                    {showPoster ? (
                        <button
                            type="button"
                            className="absolute inset-0 z-[1] flex items-center justify-center"
                            aria-label="Přehrát video"
                            onClick={handlePosterPlay}
                        >
                            <img
                                src={video.poster}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                            />
                            <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-full bg-product-500 transition-colors hover:bg-product-600">
                                <FaPlay className="ml-1 text-white" size={20} />
                            </span>
                        </button>
                    ) : null}

                    {showQualityMenu && wasPlayed ? (
                        <div
                            className={clsx(
                                'pointer-events-none absolute bottom-12 right-2 z-10 transition-opacity',
                                isQualityMenuOpen
                                    ? 'opacity-100'
                                    : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100'
                            )}
                        >
                            <div className="pointer-events-auto">
                                <VideoQualityMenu
                                    qualities={qualities}
                                    selectedQuality={selectedQuality}
                                    onSelect={handleQualityChange}
                                    isOpen={isQualityMenuOpen}
                                    onOpenChange={setIsQualityMenuOpen}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : (
                <img
                    width={1920}
                    height={1080}
                    src={video.poster}
                    alt=""
                    className={clsx(
                        'aspect-video w-full rounded-xl bg-cloud-100',
                        posterClassName || 'mb-8 mt-4'
                    )}
                />
            )}
        </div>
    );
}
